import React, { createContext, useCallback, useEffect, useState } from 'react';
import { axiosApiInstance } from 'Store';
import { calculatorModuleApiRequests, getRenovationByArea } from 'utils/api-requests/calculatorModule';
import { flipApiRequests } from 'utils/api-requests/flip';
import { CalculatorSettings } from 'utils/types/Calculator';
import { CalculatorModuleState, initialCalculatorModuleState, SaveCalculationRequest } from 'utils/types/CalculatorModule';
import { CalculateParamValue1ParamsEnum } from 'utils/types/FlipModels';

import { activeNotification } from 'components/functions/activeNotification';

const fieldNameToParamEnum: Record<string, CalculateParamValue1ParamsEnum> = {
    brokerPercent: CalculateParamValue1ParamsEnum.BROKER,
    saleBrokerPercent: CalculateParamValue1ParamsEnum.SALE_BROKER,
    maintenanceFees: CalculateParamValue1ParamsEnum.MAINTENANCE_FEES,
    rentFees: CalculateParamValue1ParamsEnum.RENT_FEES,
    renovation: CalculateParamValue1ParamsEnum.RENOVATION,
    renovationFrom1To30Amount: CalculateParamValue1ParamsEnum.RENOVATIONFROM130,
    renovationFrom30To40Amount: CalculateParamValue1ParamsEnum.RENOVATIONFROM3040,
    renovationFrom40To50Amount: CalculateParamValue1ParamsEnum.RENOVATIONFROM4050,
    renovationFrom50To60Amount: CalculateParamValue1ParamsEnum.RENOVATIONFROM5060,
    renovationFrom60PlusAmount: CalculateParamValue1ParamsEnum.RENOVATIONFROM60PLUS,
    pcc: CalculateParamValue1ParamsEnum.PCC,
    userOption: CalculateParamValue1ParamsEnum.REMAINING,
    flipTime: CalculateParamValue1ParamsEnum.FLIP_TIME,
    minProfitAmount: CalculateParamValue1ParamsEnum.MIN_PROFIT_AMOUNT,
    minProfitPercent: CalculateParamValue1ParamsEnum.MIN_PROFIT_PERCENT,
    notary: CalculateParamValue1ParamsEnum.NOTARY,
    portalFees: CalculateParamValue1ParamsEnum.PORTAL_FEES,
    photographerAmount: CalculateParamValue1ParamsEnum.PHOTOGRAPHER_AMOUNT
};

export interface CalculatorModuleContextType {
    calculatorModuleState: CalculatorModuleState;
    handleChange: (name: string, value: string | number | null) => void;
    handleSetCalculationValues: (id: number) => void;
    handleDeleteCalculation: (id: number) => void;
    getSavedCalculations: (id?: number) => void;
    clearCalculation: () => void;
    saveCalculation: (name: string) => Promise<void>;
    calculateFlip: () => Promise<void>;
    updateTotalField: (fieldName: string, value: string) => Promise<void>;
    resetCalculationField: (fieldName: string) => Promise<void>;
}

const values: CalculatorModuleContextType = {
    calculatorModuleState: {} as CalculatorModuleState,
    handleChange: () => {},
    handleSetCalculationValues: () => {},
    handleDeleteCalculation: () => {},
    getSavedCalculations: () => {},
    clearCalculation: () => {},
    saveCalculation: () => Promise.resolve(),
    calculateFlip: () => Promise.resolve(),
    updateTotalField: () => Promise.resolve(),
    resetCalculationField: () => Promise.resolve()
};

export const CalculatorModuleContext = createContext(values);

export const CalculatorModuleProvider = ({ children }: { children: React.ReactNode }) => {
    const [calculatorModuleState, setCalculatorModuleState] = useState<CalculatorModuleState>(initialCalculatorModuleState);

    useEffect(() => {
        const fetchInitialData = async () => {
            const flipDefaultValues = await flipApiRequests.getFlipDefaultValues();

            if (Object.keys(flipDefaultValues).length > 0) {
                setCalculatorModuleState((prev) => ({
                    ...prev,
                    ...(flipDefaultValues as CalculatorSettings)
                }));
            }

            await getSavedCalculations();
        };

        fetchInitialData();
    }, []);

    const handleChange = useCallback((fieldName: string, value: string | number | null) => {
        setCalculatorModuleState((state: CalculatorModuleState) => ({
            ...state,
            [fieldName]: value
        }));
    }, []);

    const getSavedCalculations = useCallback(async (id?: number) => {
        const savedCalculations = await calculatorModuleApiRequests.getSavedCalculations();

        setCalculatorModuleState((state: CalculatorModuleState) => ({
            ...state,
            savedCalculations
        }));

        if (id) {
            await handleSetCalculationValues(id);
        }
    }, []);

    const clearCalculation = useCallback(async () => {
        setCalculatorModuleState((prev) => ({
            ...initialCalculatorModuleState,
            savedCalculations: prev.savedCalculations
        }));

        const flipDefaultValues = await flipApiRequests.getFlipDefaultValues();

        if (Object.keys(flipDefaultValues).length > 0) {
            setCalculatorModuleState((prev) => ({
                ...prev,
                ...(flipDefaultValues as CalculatorSettings)
            }));
        }

        await getSavedCalculations();
    }, [getSavedCalculations]);

    const handleDeleteCalculation = useCallback(async (calculationId: number) => {
        await calculatorModuleApiRequests.deleteCalculation(calculationId);

        setCalculatorModuleState(initialCalculatorModuleState);
        getSavedCalculations();
    }, [getSavedCalculations]);

    const handleSetCalculationValues = async (id: number) => {
        const calculationData = await calculatorModuleApiRequests.getCalculation(id);

        if (calculationData) {
            setCalculatorModuleState((state: CalculatorModuleState) => ({
                ...state,
                calculationId: calculationData.filterId,
                calculationName: calculationData.name,
                currentPrice: calculationData.realPrice,
                certainPrice: calculationData.certainPrice,
                area: calculationData.area,
                address: calculationData.address || '',
                maxPurchasePrice: calculationData.maxPurchasePrice,
                profit: calculationData.profit,
                margin: calculationData.margin,
                costs: calculationData.costs,
                flipTime: calculationData.flipTime,
                minProfitAmount: calculationData.minProfitAmount,
                minProfitPercent: calculationData.minProfitPercent,
                notary: calculationData.notary,
                brokerPercent: calculationData.brokerPercent,
                rentFees: calculationData.rentFees,
                maintenanceFees: calculationData.maintenanceFees,
                portalFees: calculationData.portalFees,
                photographerAmount: calculationData.photographerAmount,
                saleBrokerPercent: calculationData.saleBrokerPercent,
                pcc: calculationData.pcc,
                userOption: calculationData.userOption ?? 0
            }));
        } else {
            setCalculatorModuleState((state: CalculatorModuleState) => ({
                ...state,
                calculationName: '',
                calculationId: null
            }));
        }
    };

    const saveCalculation = useCallback(async (name: string) => {
        if (!name.trim()) {
            activeNotification('Brak nazwy', 'Nie można zapisać kalkulacji bez nazwy', 'warning');

            return;
        }

        if (!calculatorModuleState.currentPrice) {
            activeNotification('Brak ceny inwestycji', 'Wypełnij pole "Aktualna cena" przed zapisaniem', 'warning');

            return;
        }

        if (!calculatorModuleState.certainPrice) {
            activeNotification('Brak ceny po remoncie', 'Wypełnij pole "Cena pewniak po remoncie" przed zapisaniem', 'warning');

            return;
        }

        if (!calculatorModuleState.area) {
            activeNotification('Brak metrażu', 'Wypełnij pole "Metraż inwestycji" przed zapisaniem', 'warning');

            return;
        }

        setCalculatorModuleState((prev) => ({ ...prev, isLoading: true }));

        const renovation = getRenovationByArea(calculatorModuleState);

        const payload: SaveCalculationRequest = {
            name,
            price: calculatorModuleState.currentPrice || 0,
            certainPrice: calculatorModuleState.certainPrice || 0,
            area: calculatorModuleState.area || 0,
            address: calculatorModuleState.address,
            flipTime: calculatorModuleState.flipTime,
            minProfitAmount: calculatorModuleState.minProfitAmount,
            minProfitPercent: calculatorModuleState.minProfitPercent,
            notary: calculatorModuleState.notary,
            brokerPercent: calculatorModuleState.brokerPercent,
            rentFees: calculatorModuleState.rentFees,
            maintenanceFees: calculatorModuleState.maintenanceFees,
            portalFees: calculatorModuleState.portalFees,
            photographerAmount: calculatorModuleState.photographerAmount,
            saleBrokerPercent: calculatorModuleState.saleBrokerPercent,
            pcc: calculatorModuleState.pcc,
            renovation: renovation,
            userOption: calculatorModuleState.userOption || 0
        };

        if (calculatorModuleState.calculationId != null) {
            payload.filterId = calculatorModuleState.calculationId;
        }

        const response = await calculatorModuleApiRequests.saveCalculation(payload);

        if (response) {
            const id = response.calculationId;
            setCalculatorModuleState((prev) => ({ ...prev, calculationId: id, calculationName: name }));
            await getSavedCalculations(id);
        }

        setCalculatorModuleState((prev) => ({ ...prev, isLoading: false }));
    }, [calculatorModuleState, getSavedCalculations]);

    const calculateFlip = useCallback(async () => {
        setCalculatorModuleState((prev) => ({ ...prev, isLoading: true }));

        const results = await calculatorModuleApiRequests.calculateFlip(calculatorModuleState);

        if (results) {
            setCalculatorModuleState((prev) => ({
                ...prev,
                maxPurchasePrice: results.maxPurchasePrice,
                profit: results.profit,
                margin: results.margin,
                costs: results.costs
            }));
        }

        setCalculatorModuleState((prev) => ({ ...prev, isLoading: false }));
    }, [calculatorModuleState]);

    const updateTotalField = useCallback(async (fieldName: string, value: string) => {
        setCalculatorModuleState((prev) => ({ ...prev, isLoading: true }));

        const paramEnum = fieldNameToParamEnum[fieldName as string];

        if (!paramEnum) {
            setCalculatorModuleState((prev) => ({ ...prev, isLoading: false }));

            return;
        }

        const requestData = {
            price: calculatorModuleState.currentPrice || 0,
            area: calculatorModuleState.area || 0,
            paramValue: value ? parseFloat(value.replace(/\s/g, '').replace(',', '.')) : 0,
            maxPurchasePrice: calculatorModuleState.maxPurchasePrice || 0,
            certainPrice: calculatorModuleState.certainPrice || 0
        };

        const calculatedValue = await axiosApiInstance.post(
            `/calculation/value/${paramEnum}`,
            requestData
        ).then((resp) => resp.data);

        setCalculatorModuleState((prev) => ({
            ...prev,
            [fieldName]: calculatedValue.toString()
        }));

        const results = await calculatorModuleApiRequests.calculateFlip({
            ...calculatorModuleState,
            [fieldName]: calculatedValue.toString()
        });

        if (results) {
            setCalculatorModuleState((prev) => ({
                ...prev,
                maxPurchasePrice: results.maxPurchasePrice,
                profit: results.profit,
                margin: results.margin,
                costs: results.costs
            }));
        }

        setCalculatorModuleState((prev) => ({ ...prev, isLoading: false }));
    }, [calculatorModuleState]);

    const resetCalculationField = useCallback(async (fieldName: string) => {
        setCalculatorModuleState((prev) => ({ ...prev, isLoading: true }));

        const globalSettings = await flipApiRequests.getFlipDefaultValues();

        if (Object.keys(globalSettings).length === 0) {
            activeNotification('Błąd', 'Nie udało się pobrać globalnych ustawień', 'warning');
            setCalculatorModuleState((prev) => ({ ...prev, isLoading: false }));

            return;
        }

        const globalValue = globalSettings[fieldName as keyof typeof globalSettings];

        setCalculatorModuleState((prev) => ({
            ...prev,
            [fieldName]: globalValue
        }));

        const results = await calculatorModuleApiRequests.calculateFlip({
            ...calculatorModuleState,
            [fieldName]: globalValue
        });

        if (results) {
            setCalculatorModuleState((prev) => ({
                ...prev,
                maxPurchasePrice: results.maxPurchasePrice,
                profit: results.profit,
                margin: results.margin,
                costs: results.costs
            }));
        }

        activeNotification('Zresetowano', 'Pole zostało zresetowane do wartości domyślnej', 'success');
        setCalculatorModuleState((prev) => ({ ...prev, isLoading: false }));
    }, [calculatorModuleState]);

    return (
        <CalculatorModuleContext.Provider
            value={{
                calculatorModuleState,
                handleChange,
                handleSetCalculationValues,
                handleDeleteCalculation,
                getSavedCalculations,
                clearCalculation,
                saveCalculation,
                calculateFlip,
                updateTotalField,
                resetCalculationField
            }}
        >
            {children}
        </CalculatorModuleContext.Provider>
    );
};

export default CalculatorModuleContext;
